import { ClassroomQuery } from "@outschool/gql-frontend-generated";
import { CreateAssignmentClassPostInput } from "@outschool/gql-frontend-generated/src";
import {
  DocumentNode,
  getQueryNames,
  gql,
  useMutation
} from "@outschool/ui-apollo";
import {
  AttachmentFragmentFragment,
  ClassPostFragments,
  ClassPost_MentionNamesFragment,
  ClubQuery
} from "@outschool/ui-components-classroom";
import React from "react";

import {
  SectionAssignmentClassPostFragmentGql,
  sectionPostManagementQuery
} from "../../../client/routes/SectionPostsPage/graphql";
import { addTypeName, validOptimisticResponse } from "../ApolloClient";

export const addClassPostMutation = gql`
  mutation addClassPost($classPostInput: ClassPostInput!, $plainText: Boolean) {
    addClassPost(classPostInput: $classPostInput) {
      ...ClassPost_classPost
    }
  }
  ${ClassPostFragments.classPost}
`;

const isClubQuery = (q: ClubQuery | ClassroomQuery | null): q is ClubQuery =>
  // prettier-ignroe
  // @ts-ignore
  !!q && !!q["club"];

export interface AddClassPostInput {
  text: string;
  textMimeType?: string;
  video?: AttachmentFragmentFragment;
  attachments?: AttachmentFragmentFragment[];
  mentions?: ClassPost_MentionNamesFragment[];
  title?: string;
  publishAt?: Date;
  backgroundColor?: string;
}

export function useAddClassPostMutation({
  sender,
  classroomQueryVariables,
  queryToUpdate
}: {
  sender:
    | { __typename?: "User"; uid: string; name: string; photo?: string | null }
    | {
        __typename?: "CurrentLearner";
        uid: string;
        name?: string | null;
        avatar?: string | null;
      };
  classroomQueryVariables: any;
  queryToUpdate: DocumentNode;
}) {
  const [mutate, { error }] = useMutation(addClassPostMutation);
  const addClassPost = React.useMemo(
    () => (input: AddClassPostInput) => {
      const {
        text,
        textMimeType,
        attachments,
        video,
        publishAt,
        mentions = [],
        title = null,
        backgroundColor = null
      } = input;
      const attachmentUids = attachments ? attachments.map(a => a.uid) : [];
      const videoUid = video ? video.uid : undefined;
      const mentionsUids = mentions ? mentions.map(m => m.id) : [];
      const isScheduledPost = Boolean(publishAt);
      const classPostInput = {
        sectionUid: classroomQueryVariables.sectionUid,
        publishAt,
        messageContent: {
          text,
          textMimeType,
          attachmentUids,
          videoUid,
          mentionsUids
        },
        title,
        backgroundColor
      };
      const commentsPreview = {
        __typename: "CommentsPreview",
        comments: [],
        remainingCommentsCount: 0
      };
      return mutate({
        variables: { classPostInput, plainText: false },
        optimisticResponse: validOptimisticResponse({
          __typename: "Mutation",
          addClassPost: {
            __typename: "ClassPost",
            isWelcomePost: false,
            uid: new Date(),
            sender: {
              __typename: "MessageSender",
              uid: sender.uid,
              name: sender.name,
              photo: "photo" in sender ? sender.photo : null,
              isLearner: sender.__typename === "CurrentLearner",
              avatar: "avatar" in sender ? sender.avatar : null
            },
            messageContent: {
              __typename: "MessageContent",
              uid: new Date(),
              text,
              attachments: addTypeName<AttachmentFragmentFragment>(
                "Attachment",
                attachments ?? []
              ),
              video: video
                ? addTypeName<AttachmentFragmentFragment>("Attachment", video)
                : null,
              editedAt: null
            },
            publishAt: (classPostInput.publishAt || new Date()).toISOString(),
            commentsPreview,
            title,
            promotedAt: null,
            pinnedAt: null,
            mentionedEnrollmentUids: mentionsUids,
            backgroundColor,
            score: 0
          }
        }),
        update(store, { data }) {
          if (!data?.addClassPost) {
            console.error("AddClassPost data is null");
            return;
          }
          const { addClassPost } = data;
          const variables = classroomQueryVariables;
          const classroomData = store.readQuery<ClassroomQuery | ClubQuery>({
            query: queryToUpdate,
            variables
          });
          const dataKey = isClubQuery(classroomData) ? "club" : "classroom";
          // @ts-ignore
          const clubOrClassroom = classroomData?.[dataKey];
          if (!clubOrClassroom?.posts) {
            // We do not have a place in our store to put the new post. Exit
            // and rely on a future ClassroomQuery to fill this updated data.
            return;
          }
          const newClubOrClassroom = { ...clubOrClassroom };
          const newData = { ...classroomData, [dataKey]: newClubOrClassroom };
          const newPost = { ...addClassPost, commentsPreview };
          if (isScheduledPost) {
            if (newClubOrClassroom.unpublishedPosts) {
              newClubOrClassroom.unpublishedPosts = [
                newPost,
                ...newClubOrClassroom.unpublishedPosts
              ];
            }
          } else {
            newClubOrClassroom.posts = [newPost, ...newClubOrClassroom.posts];
          }
          store.writeQuery({
            query: queryToUpdate,
            variables,
            data: newData
          });
        }
      });
    },
    [mutate, classroomQueryVariables, queryToUpdate, sender]
  );
  return { addClassPost, error };
}

export const addClassPostForLessonMutation = gql`
  mutation addClassPostForLesson(
    $classPostForLessonInput: ClassPostForLessonInput!
    $plainText: Boolean
  ) {
    addClassPostForLesson(classPostForLessonInput: $classPostForLessonInput) {
      ...ClassPost_classPost
    }
  }
  ${ClassPostFragments.classPost}
`;

export function useAddClassPostMutationForLesson({
  updateQueryVariables
}: {
  updateQueryVariables: {
    sectionUid: string;
    lessonUid?: string;
  };
}): [any, { error?: any; loading: boolean }] {
  const [mutate, mutationFields] = useMutation(addClassPostForLessonMutation);
  const addClassPostForLesson = ({
    classPostForLessonInput,
    plainText
  }: $TSFixMe) => {
    const { sectionUid, lessonUid } = updateQueryVariables;
    const {
      text,
      textMimeType,
      attachments,
      video,
      publishAt,
      mentions = [],
      title = null,
      backgroundColor = null
    } = classPostForLessonInput;
    const attachmentUids = attachments
      ? attachments.map((a: $TSFixMe) => a.uid)
      : [];
    const videoUid = video ? video.uid : undefined;
    const mentionsUids = mentions ? mentions.map((m: $TSFixMe) => m.id) : [];
    const classPostInput = {
      sectionUid,
      lessonUid,
      publishAt,
      messageContent: {
        text,
        textMimeType,
        attachmentUids,
        videoUid,
        mentionsUids
      },
      title,
      backgroundColor
    };
    return mutate({
      variables: { classPostForLessonInput: classPostInput, plainText },
      update(store, { data }) {
        if (!data?.addClassPostForLesson) {
          return;
        }

        if (updateQueryVariables.lessonUid) {
          const { addClassPostForLesson } = data;
          const variables = {
            sectionUid,
            plainText: true
          };
          const sectionData = store.readQuery({
            query: sectionPostManagementQuery,
            variables
          });

          const newData: any = deepClone(sectionData);
          const lessons = newData?.section?.syllabus?.lessons;

          if (lessons?.length > 0) {
            const lesson = lessons.find(
              (lesson: $TSFixMe) => lesson.uid === lessonUid
            );
            const newClassPost = deepClone(addClassPostForLesson);
            newClassPost.lesson = lesson;
            newClassPost.lessonPost = null;

            lesson.classPosts = [newClassPost, ...(lesson.classPosts || [])];
            store.writeQuery({
              query: sectionPostManagementQuery,
              variables,
              data: newData
            });
          }
        }
      }
    });
  };

  return [addClassPostForLesson, mutationFields];
}
const ADD_ASSIGNMENT_CLASS_POST = gql`
  mutation addAssignmentClassPost(
    $createAssignmentClassPostInput: CreateAssignmentClassPostInput!
    $plainText: Boolean
  ) {
    addAssignmentClassPost(
      createAssignmentClassPostInput: $createAssignmentClassPostInput
    ) {
      ...Section_AssignmentClassPost
    }
  }
  ${SectionAssignmentClassPostFragmentGql}
`;
const deepClone = (obj: $TSFixMe) => JSON.parse(JSON.stringify(obj));
export const useAddAssignmentClassPostMutation = ({
  sectionUid,
  lessonUid
}: $TSFixMe): [
  (input: Partial<CreateAssignmentClassPostInput>) => any,
  { loading: boolean; error?: any }
] => {
  const [mutate, gqlFields] = useMutation(ADD_ASSIGNMENT_CLASS_POST);
  const addAssignmentClassPost = (
    input: Partial<CreateAssignmentClassPostInput>
  ) => {
    return mutate({
      variables: {
        createAssignmentClassPostInput: {
          sectionUid,
          lessonUid,
          ...input
        },
        plainText: true
      },
      update: (store, { data }) => {
        if (!data?.addAssignmentClassPost) {
          return;
        }

        if (sectionUid) {
          const { addAssignmentClassPost } = data;
          const variables = {
            sectionUid,
            plainText: true
          };
          const sectionData = store.readQuery({
            query: sectionPostManagementQuery,
            variables
          });

          const newData: any = deepClone(sectionData);
          const lessons = newData?.section?.syllabus?.lessons;

          if (lessons?.length > 0) {
            const newAssignmentClassPost = deepClone(addAssignmentClassPost);
            const lesson = lessons.find(
              (lesson: $TSFixMe) =>
                lesson.uid === newAssignmentClassPost.lesson.uid
            );

            lesson.assignmentClassPosts = [
              newAssignmentClassPost,
              ...(lesson.assignmentClassPosts || [])
            ];

            store.writeQuery({
              query: sectionPostManagementQuery,
              variables,
              data: newData
            });
          }
        }
      }
    });
  };
  return [addAssignmentClassPost, gqlFields];
};

const DELETE_ASSIGNMENT_CLASS_POST = gql`
  mutation deleteAssignmentClassPost($uid: ID!, $plainText: Boolean) {
    deleteAssignmentClassPost(uid: $uid) {
      ...Section_AssignmentClassPost
    }
  }
  ${SectionAssignmentClassPostFragmentGql}
`;

export const useDeleteAssignmentClassPostMutation = ({
  sectionUid,
  lessonUid,
  uid
}: $TSFixMe): [() => any, { loading: boolean; error?: any }] => {
  const [mutate, gqlFields] = useMutation(DELETE_ASSIGNMENT_CLASS_POST);
  const deleteAssignmentClassPost = () => {
    return mutate({
      variables: { uid, plainText: true },
      update: (store, { data }) => {
        if (!data?.deleteAssignmentClassPost) {
          return;
        }

        if (sectionUid) {
          const { deleteAssignmentClassPost } = data;
          const variables = {
            sectionUid,
            plainText: true
          };
          const sectionData = store.readQuery({
            query: sectionPostManagementQuery,
            variables
          });

          const newData: any = deepClone(sectionData);
          const lessons = newData?.section?.syllabus?.lessons;

          if (lessons?.length > 0) {
            const lesson = lessons.find(
              (lesson: $TSFixMe) => lesson.uid === lessonUid
            );
            lesson.assignmentClassPosts = (
              lesson.assignmentClassPosts || []
            ).filter(
              (post: $TSFixMe) => post.uid !== deleteAssignmentClassPost.uid
            );
            store.writeQuery({
              query: sectionPostManagementQuery,
              variables,
              data: newData
            });
          }
        }
      },
      refetchQueries: getQueryNames([sectionPostManagementQuery]),
      awaitRefetchQueries: true
    });
  };
  return [deleteAssignmentClassPost, gqlFields];
};

const UPDATE_ASSIGNMENT_CLASS_POST = gql`
  mutation updateAssignmentClassPost(
    $uid: ID!
    $updateAssignmentClassPostInput: UpdateAssignmentClassPostInput!
    $plainText: Boolean
  ) {
    updateAssignmentClassPost(
      uid: $uid
      updateAssignmentClassPostInput: $updateAssignmentClassPostInput
    ) {
      ...Section_AssignmentClassPost
    }
  }
  ${SectionAssignmentClassPostFragmentGql}
`;

export const useUpdateAssignmentClassPostMutation = ({
  sectionUid,
  uid
}: {
  sectionUid: string;
  uid: string;
}): [
  (input: Partial<CreateAssignmentClassPostInput>) => any,
  { loading: boolean; error?: any }
] => {
  const [mutate, gqlFields] = useMutation(UPDATE_ASSIGNMENT_CLASS_POST);
  const updateAssignmentClassPost = (
    input: Partial<CreateAssignmentClassPostInput>
  ) => {
    return mutate({
      variables: {
        uid,
        updateAssignmentClassPostInput: input
      },
      refetchQueries: [
        {
          query: sectionPostManagementQuery,
          variables: {
            sectionUid,
            plainText: true
          }
        }
      ],
      awaitRefetchQueries: true
    });
  };
  return [updateAssignmentClassPost, gqlFields];
};
